/**
 * Map CasaMapa free-text search (`q`) into structured filters.
 * Deterministic keyword/alias matching — no NLP dependencies.
 */

import {
  CASAMAPA_METRO_MUNICIPALITIES,
  normalizeCasaMapaMunicipality,
} from "./casaMapaMunicipality";
import { normalizeCasaMapaNeighborhood } from "./casaMapaNeighborhood";

const STOPWORDS = new Set([
  "a",
  "an",
  "and",
  "at",
  "cerca",
  "con",
  "de",
  "del",
  "en",
  "for",
  "in",
  "la",
  "las",
  "los",
  "near",
  "para",
  "por",
  "the",
  "un",
  "una",
  "with",
  "y",
]);

const LISTING_TYPE_PATTERNS = Object.freeze([
  { type: "sale", pattern: /\b(?:for\s+sale|venta|comprar|buy|sale)\b/i },
  { type: "rent", pattern: /\b(?:for\s+rent|arriendo|arrendar|alquiler|rent|renta)\b/i },
]);

const PROPERTY_TYPE_PATTERNS = Object.freeze([
  {
    type: "country_house",
    pattern: /\b(?:country\s*hous(?:e|es)|casa\s*campestre|finca\s*campestre)\b/i,
  },
  { type: "apartment", pattern: /\b(?:apartments?|apartamentos?|aptos?|apto)\b/i },
  { type: "house", pattern: /\b(?:houses?|casas?|homes?)\b/i },
  {
    type: "studio",
    pattern: /\b(?:studios?|apartaestudios?|apart[ao]\s*estudios?)\b/i,
  },
  { type: "penthouse", pattern: /\b(?:penthouses?)\b/i },
  { type: "duplex", pattern: /\b(?:duplex)\b/i },
  { type: "farm", pattern: /\b(?:farms?|fincas?)\b/i },
  { type: "lot", pattern: /\b(?:lots?|lotes?|lands?|terrenos?)\b/i },
  { type: "office", pattern: /\b(?:offices?|oficinas?)\b/i },
  {
    type: "commercial",
    pattern: /\b(?:commercial|comercial(?:es)?|locales?)\b/i,
  },
]);

const BEDROOM_PATTERNS = Object.freeze([
  /\b(\d+)\s*(?:bed(?:room)?s?|habitaciones?|alcobas?|br)\b/i,
  /\b(?:bed(?:room)?s?|habitaciones?|alcobas?)\s*[:=]?\s*(\d+)\b/i,
]);

const BATHROOM_PATTERNS = Object.freeze([
  /\b(\d+)\s*(?:bath(?:room)?s?|banos?)\b/i,
  /\b(?:bath(?:room)?s?|banos?)\s*[:=]?\s*(\d+)\b/i,
]);

const PARKING_PATTERNS = Object.freeze([
  /\b(\d+)\s*(?:parking|garages?|parqueaderos?)\b/i,
  /\b(?:parking|garage|parqueadero)\s*[:=]?\s*(\d+)\b/i,
]);

const ESTRATO_PATTERNS = Object.freeze([
  /\bestrato\s*[:=]?\s*([1-6])\b/i,
  /\bstratum\s*[:=]?\s*([1-6])\b/i,
]);

function stripAccents(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function takeMatch(working, pattern) {
  const match = working.match(pattern);
  if (!match || match.index == null) {
    return { working, match: null };
  }
  const next = `${working.slice(0, match.index)} ${working.slice(match.index + match[0].length)}`
    .replace(/\s+/g, " ")
    .trim();
  return { working: next, match };
}

function firstPositiveInt(patterns, working) {
  for (const pattern of patterns) {
    const { working: next, match } = takeMatch(working, pattern);
    if (!match) continue;
    const raw = match[1] ?? match[2];
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) {
      return { working: next, value: Math.floor(n) };
    }
  }
  return null;
}

function buildMunicipalityPatterns() {
  const aliases = new Map();
  for (const name of CASAMAPA_METRO_MUNICIPALITIES) {
    const canonical = normalizeCasaMapaMunicipality(name);
    if (!canonical) continue;
    for (const variant of [name, canonical, stripAccents(name), stripAccents(canonical)]) {
      const key = stripAccents(variant).toLowerCase().trim();
      if (key) aliases.set(key, canonical);
    }
  }
  aliases.set("medellin", "Medellín");
  aliases.set("itagui", "Itagüí");
  aliases.set("la estrella", "La Estrella");

  return [...aliases.entries()]
    .sort((a, b) => b[0].length - a[0].length)
    .map(([alias, canonical]) => ({
      canonical,
      pattern: new RegExp(`\\b${escapeRegExp(alias).replace(/\\ /g, "\\s+")}\\b`, "i"),
    }));
}

const MUNICIPALITY_PATTERNS = buildMunicipalityPatterns();

/**
 * @param {string|null|undefined} query
 */
export function parseCasaMapaFreeTextQuery(query) {
  const original = String(query || "").trim().replace(/\s+/g, " ");
  const empty = {
    listingType: null,
    propertyTypes: [],
    municipalities: [],
    neighborhood: null,
    bedroomsMin: null,
    bathroomsMin: null,
    parkingMin: null,
    estratos: [],
    residualQuery: original || null,
    matched: false,
  };
  if (!original) {
    return empty;
  }

  // Accent-fold so "Medellín" / "medellin" share indices with removals.
  let working = stripAccents(original).replace(/\s+/g, " ").trim();
  const propertyTypes = [];
  const municipalities = [];
  const estratos = [];
  let listingType = null;
  let neighborhood = null;
  let bedroomsMin = null;
  let bathroomsMin = null;
  let parkingMin = null;
  let matched = false;

  for (const entry of LISTING_TYPE_PATTERNS) {
    const taken = takeMatch(working, entry.pattern);
    if (!taken.match) continue;
    listingType = entry.type;
    working = taken.working;
    matched = true;
    break;
  }

  for (const entry of PROPERTY_TYPE_PATTERNS) {
    const taken = takeMatch(working, entry.pattern);
    if (!taken.match) continue;
    if (!propertyTypes.includes(entry.type)) {
      propertyTypes.push(entry.type);
    }
    working = taken.working;
    matched = true;
  }

  for (const entry of MUNICIPALITY_PATTERNS) {
    const taken = takeMatch(working, entry.pattern);
    if (!taken.match) continue;
    if (!municipalities.includes(entry.canonical)) {
      municipalities.push(entry.canonical);
    }
    working = taken.working;
    matched = true;
  }

  const beds = firstPositiveInt(BEDROOM_PATTERNS, working);
  if (beds) {
    bedroomsMin = beds.value;
    working = beds.working;
    matched = true;
  }

  const baths = firstPositiveInt(BATHROOM_PATTERNS, working);
  if (baths) {
    bathroomsMin = baths.value;
    working = baths.working;
    matched = true;
  }

  const parking = firstPositiveInt(PARKING_PATTERNS, working);
  if (parking) {
    parkingMin = parking.value;
    working = parking.working;
    matched = true;
  }

  for (const pattern of ESTRATO_PATTERNS) {
    const taken = takeMatch(working, pattern);
    if (!taken.match?.[1]) continue;
    const n = Number(taken.match[1]);
    if (n >= 1 && n <= 6 && !estratos.includes(n)) {
      estratos.push(n);
    }
    working = taken.working;
    matched = true;
  }

  const residualTokens = working
    .replace(/[^a-zA-Z0-9\s#-]/g, " ")
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean)
    .filter((token) => !STOPWORDS.has(token.toLowerCase()));

  // Only promote well-known barrio aliases (e.g. poblado → El Poblado),
  // never invent neighborhoods from leftover free text.
  if (residualTokens.length) {
    for (let size = Math.min(3, residualTokens.length); size >= 1; size -= 1) {
      const candidateTokens = residualTokens.slice(0, size);
      const joined = candidateTokens.join(" ");
      const candidate = normalizeCasaMapaNeighborhood(joined);
      const compactCandidate = stripAccents(candidate || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "");
      const compactJoined = stripAccents(joined)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "");
      if (candidate && compactCandidate !== compactJoined) {
        neighborhood = candidate;
        residualTokens.splice(0, size);
        matched = true;
        break;
      }
    }
  }

  return {
    listingType,
    propertyTypes,
    municipalities,
    neighborhood,
    bedroomsMin,
    bathroomsMin,
    parkingMin,
    estratos,
    residualQuery: residualTokens.join(" ").trim() || null,
    matched,
  };
}

/**
 * Merge free-text inferences into search state without overriding explicit filters.
 */
export function applyCasaMapaFreeTextQueryToSearchState(state = {}, inferred = null) {
  const next = { ...state };
  const parsed =
    inferred ||
    (typeof state.q === "string" && state.q.trim()
      ? parseCasaMapaFreeTextQuery(state.q)
      : null);
  if (!parsed?.matched) {
    return next;
  }

  let applied = false;

  if (!next.listingType && parsed.listingType) {
    next.listingType = parsed.listingType;
    applied = true;
  }
  if (
    (!Array.isArray(next.propertyTypes) || next.propertyTypes.length === 0) &&
    parsed.propertyTypes.length
  ) {
    next.propertyTypes = [...parsed.propertyTypes];
    applied = true;
  }
  if (
    (!Array.isArray(next.municipalities) || next.municipalities.length === 0) &&
    !next.municipality &&
    parsed.municipalities.length
  ) {
    next.municipalities = [...parsed.municipalities];
    next.municipality = parsed.municipalities[0];
    applied = true;
  }
  if (!next.neighborhood && parsed.neighborhood) {
    next.neighborhood = parsed.neighborhood;
    applied = true;
  }
  if (next.bedroomsMin == null && parsed.bedroomsMin != null) {
    next.bedroomsMin = parsed.bedroomsMin;
    applied = true;
  }
  if (next.bathroomsMin == null && parsed.bathroomsMin != null) {
    next.bathroomsMin = parsed.bathroomsMin;
    applied = true;
  }
  if (next.parkingMin == null && parsed.parkingMin != null) {
    next.parkingMin = parsed.parkingMin;
    applied = true;
  }
  if (
    (!Array.isArray(next.estratos) || next.estratos.length === 0) &&
    parsed.estratos.length
  ) {
    next.estratos = [...parsed.estratos];
    applied = true;
  }

  if (applied) {
    next.q = parsed.residualQuery;
  }
  return next;
}
